import { ArrowForward } from '@mui/icons-material'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import LockPersonIcon from '@mui/icons-material/LockPerson'
import { Box, Button, Card, Divider, Skeleton, Tooltip, Typography } from '@mui/material'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import { PieChart } from '@mui/x-charts/PieChart'
import { SparkLineChart } from '@mui/x-charts/SparkLineChart'
import { useEffect, useState } from 'react'
import useFetchWidgetsHojasGasto from '../hooks/useFetchWidgetsHojasGasto'
import AddExpenseButton from './AddExpenseButton'
import { HojasGastoAddModal } from './HojasGastoAddModal'

const WidgetsHojasGasto = ({ sxBox = {}, sxCardBox = {}, sxButtonsBox = {} }) => {
  const { response, error, loading } = useFetchWidgetsHojasGasto()

  const [ultimaHoja, setUltimaHoja] = useState({})
  const [fechaInicio, setFechaInicio] = useState(null)
  const [fechaFin, setFechaFin] = useState(null)
  const [status, setStatus] = useState(null)
  const [importeTotal, setImporteTotal] = useState(0)
  const [importesPorConcepto, setImportesPorConcepto] = useState([])
  const [ultimosImportes, setUltimosImportes] = useState([])
  const [pieData, setPieData] = useState([])
  const [hideLegend, setHideLegend] = useState(true)
  const [sparkLineData, setSparkLineData] = useState([])

  useEffect(() => {
    if (!loading && !error && response) {
      setUltimaHoja(response.ultima_hoja || {})

      if (response?.ultima_hoja) {
        const fechaInicioStr = response.ultima_hoja.fecha_inicio
        const [sy, sm, sd] = fechaInicioStr ? fechaInicioStr.split('-') : []
        setFechaInicio(fechaInicioStr ? new Date(sy, sm - 1, sd) : null)
        const fechaFinStr = response.ultima_hoja.fecha_fin
        const [ey, em, ed] = fechaFinStr ? fechaFinStr.split('-') : []
        setFechaFin(fechaFinStr ? new Date(ey, em - 1, ed) : null)
        setStatus(response.ultima_hoja?.status || null)
      }

      setImporteTotal(response.importe_total || 0)
      setImportesPorConcepto(response.importes_por_concepto || [])
      setPieData(
        response?.importes_por_concepto?.map((c) => {
          return { label: c.concepto_nombre, value: c.total_importe }
        }) || [],
      )
      setUltimosImportes(response.ultimos_importes || [])
      setSparkLineData(response?.ultimos_importes?.map((i) => i.importe) || [])
    }
  }, [response])

  const formatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  })
  const dateFormatter = new Intl.DateTimeFormat('es-ES')

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap', overflowX: 'hidden', ...sxBox }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', ...sxCardBox }}>
        <Card key="widget-num-gastos-mes" sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h5" sx={{ mb: 0 }}>
                Última hoja
              </Typography>
              {loading ? (
                <Skeleton variant="rounded" width={150} height={25} />
              ) : (
                <Typography>
                  {fechaInicio
                    ? `${dateFormatter.format(fechaInicio)} - ${dateFormatter.format(fechaFin)}`
                    : ''}
                </Typography>
              )}
            </Box>
            {loading ? (
              <Skeleton variant="rectangular" height={20} width={20} />
            ) : !status ? (
              ''
            ) : status === '0500' ? (
              <Tooltip title="Abierta">
                <LockOpenIcon color="success" />
              </Tooltip>
            ) : status === '1500' ? (
              <Tooltip title="En validación" color="warning">
                <LockPersonIcon />
              </Tooltip>
            ) : (
              <Tooltip title="Cerrada">
                <LockIcon color="error" />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography>Número gastos</Typography>
              {loading ? (
                <Skeleton variant="rounded" width={100} height={50} />
              ) : (
                <Typography variant="h4">{ultimaHoja?.gastos ?? 0}</Typography>
              )}
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography>Total gastos</Typography>
              {loading ? (
                <Skeleton variant="rounded" width={100} height={50} />
              ) : (
                <Typography variant="h4">{formatter.format(ultimaHoja?.importe ?? 0)}</Typography>
              )}
            </Box>
          </Box>
        </Card>
        <Card
          key="widget-total-gastos"
          sx={{
            p: 2,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h5">Total</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            {loading ? (
              <Skeleton variant="rounded" width={150} height={50} />
            ) : (
              <Typography variant="h4">{formatter.format(importeTotal)}</Typography>
            )}
            {loading ? (
              <Skeleton variant="rectangular" width={200} height={50} />
            ) : (
              <SparkLineChart
                data={sparkLineData}
                valueFormatter={(value) => formatter.format(value)}
                height={50}
                width={200}
                showHighlight={true}
                showTooltip={true}
              />
            )}
          </Box>
        </Card>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...sxButtonsBox }}>
        <AddExpenseButton
          id_hoja={ultimaHoja?.id}
          fechaInicio={ultimaHoja?.fechaInicio}
          fechaFin={ultimaHoja?.fechaFin}
        />
        <Button
          variant="contained"
          color="primary"
          href={`/hojasgasto/${ultimaHoja?.id}`}
          startIcon={<ArrowForward />}
        >
          Última hoja
        </Button>
        <Button variant="contained" color="primary" href="/hojasgasto" startIcon={<ArrowForward />}>
          Lista de gastos
        </Button>
        <HojasGastoAddModal listOnClose={false} sxButton={{ ml: 0 }} />
      </Box>
      <Box sx={{ position: 'relative' }}>
        <FormControlLabel
          control={
            <Switch
              checked={!hideLegend}
              onChange={() => setHideLegend(!hideLegend)}
              size="small"
            />
          }
          label="Leyenda"
          sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
        />
        {loading ? (
          <Skeleton variant="circular" width={260} height={260} />
        ) : (
          <PieChart
            series={[
              {
                data: pieData,
                cx: hideLegend ? 150 : 150,
                innerRadius: 64,
                cornerRadius: 4,
              },
            ]}
            slotProps={{
              legend: { hidden: hideLegend },
              direction: 'row',
              position: { vertical: 'bottom', horizontal: 'middle' },
            }}
            height={260}
            width={hideLegend ? 350 : 600}
          />
        )}
      </Box>
    </Box>
  )
}

export default WidgetsHojasGasto
